import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { 
  CalendarIcon, 
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  category: string;
  featured: boolean;
  readTime: string;
  filePath: string;
  innerImage: string;
}

interface ArticleWithContent extends NewsArticle {
  content: string;
}

function NewsDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleWithContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadArticle(slug);
    }
  }, [slug]);

  const loadArticle = async (articleSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch news articles from /data/news.json
      const newsResponse = await fetch('/data/news.json');
      if (!newsResponse.ok) {
        throw new Error(`Failed to fetch news data: ${newsResponse.status}`);
      }
      
      const newsData = await newsResponse.json();
      const articleMeta = newsData.articles?.find((a: NewsArticle) => a.slug === articleSlug);
      
      if (!articleMeta) {
        setError('Article not found');
        setLoading(false);
        return;
      }

      // Load markdown content from the filePath specified in the article
      let content = '';
      try {
        const contentResponse = await fetch(articleMeta.filePath);
        if (contentResponse.ok) {
          const fullContent = await contentResponse.text();
          // Remove frontmatter from content (everything before and including the second ---)
          const contentWithoutFrontmatter = fullContent.replace(/^---[\s\S]*?---\s*/, '');
          content = contentWithoutFrontmatter;
        } else {
          console.warn(`Could not load content from ${articleMeta.filePath}`);
          content = articleMeta.excerpt || 'Content not available.';
        }
      } catch (error) {
        console.warn(`Error loading content: ${error}`);
        content = articleMeta.excerpt || 'Content not available.';
      }

      setArticle({
        ...articleMeta,
        content
      });
    } catch (error) {
      console.error('Error loading article:', error);
      setError(error instanceof Error ? error.message : 'Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white min-h-screen page-transition">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 py-24">
          <div className="text-center py-16">
            <div className="mx-auto w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
            <p className="text-neutral-500 text-lg">Loading article...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white min-h-screen page-transition">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 py-24">
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <CalendarIcon className="h-8 w-8 text-red-400" />
            </div>
            <p className="text-red-500 text-lg mb-4">{error}</p>
            <Link
              to="/news"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to News
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="bg-white min-h-screen page-transition">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 py-24">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold text-neutral-900 mb-4">Article not found</h1>
            <p className="text-neutral-600 mb-8">
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Link
              to="/news"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to News
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen page-transition">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/40 to-black/20" />
        </div>
        
        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8 py-24 lg:py-32">
          {/* Back Button */}
          <Link
            to="/news"
            className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to News
          </Link>
          
          {/* Article Meta */}
          <div className="flex items-center space-x-4 text-white/70 text-sm mb-6">
            <div className="flex items-center">
              <CalendarIcon className="h-4 w-4 mr-1" />
              {formatDate(article.date)}
            </div>
            <span>•</span>
            <span className="bg-primary-600/20 text-primary-300 px-3 py-1 rounded-full text-xs font-medium">
              {article.category}
            </span>
            <span>•</span>
            <span>{article.readTime}</span>
          </div>
          
          {/* Title */}
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
            {article.title}
          </h1>
          
          {/* Excerpt */}
          <p className="mt-6 text-xl text-white/90 leading-relaxed max-w-3xl">
            {article.excerpt}
          </p>
        </div>
      </div>

      {/* Article Content */}
      <div className="mx-auto max-w-4xl px-6 lg:px-8 py-16">
        <article className="prose prose-lg prose-neutral max-w-none">
          {/* Inner Image */}
          {article.innerImage && (
            <div className="mb-8 rounded-2xl overflow-hidden">
              <img
                src={article.innerImage}
                alt={article.title}
                className="w-full h-auto"
              />
            </div>
          )}
            {/* Markdown Content */}
          <div className="prose prose-lg prose-neutral max-w-none prose-headings:text-neutral-900 prose-headings:font-bold prose-p:text-neutral-700 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:no-underline hover:prose-a:text-primary-700 prose-strong:text-neutral-900 prose-ul:text-neutral-700 prose-ol:text-neutral-700">
            <ReactMarkdown>
              {article.content}
            </ReactMarkdown>
          </div>
        </article>
        
        {/* Article Footer */}
        <div className="mt-16 pt-8 border-t border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-neutral-500">
              Published on {formatDate(article.date)}
            </div>
            <Link
              to="/news"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold text-sm transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to News
            </Link>
          </div>
        </div>      </div>
    </div>
  );
}

export default NewsDetail;